import React, { useEffect, useRef, useState } from "react";
import { axiosMaster } from "../../Components/api/axiosInstance";



/* ✅ Title Case */
const toTitleCase = (text) => {
  if (!text) return "";
  return text
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

const AddEditGroupMaster = ({ editingData, onClose, onSuccess }) => {
  /* ================= USER FROM LOCALSTORAGE ================= */
  const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  const userId = storedUser?.userId || 0;

  /* ================= STATE ================= */
  const [form, setForm] = useState({
    id: "",
    groupId: 0,
    groupName: "",
    description: "",
    status: true,
  });

  /* ================= REFS ================= */
  const nameRef = useRef(null);
  const descRef = useRef(null);
  const statusRef = useRef(null);
  const saveRef = useRef(null);

  /* ================= LOAD EDIT ================= */
  useEffect(() => {
    if (editingData) {
      setForm({ ...editingData });
    }
  }, [editingData]);

  /* ================= AUTO FOCUS ================= */
  useEffect(() => {
    const t = setTimeout(() => {
      if (nameRef.current) {
        nameRef.current.focus();

        // ✅ auto select in edit mode
        if (editingData?.groupName) {
          nameRef.current.select();
        }
      }
    }, 120);

    return () => clearTimeout(t);
  }, [editingData]);

  /* ================= AUTO SELECT ON FOCUS ================= */
  const handleAutoSelect = (ref) => {
    if (!editingData) return;

    setTimeout(() => {
      ref?.current?.select();
    }, 0);
  };

  /* ================= KEY NAV ================= */
  const handleKeyDown = (e, nextRef, prevRef) => {
    // 🚫 DO NOT block space
    if (e.key === " ") return;

    if (e.key === "Enter") {
      e.preventDefault();
      nextRef?.current?.focus();
    }

    if (e.key === "Escape") {
      e.preventDefault();

      if (prevRef?.current) {
        prevRef.current.focus();
      } else {
        onClose?.();
      }
    }
  };

  /* ================= CHANGE ================= */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  /* ================= FORMAT ================= */
  const handleBlurFormat = (e) => {
    const { name, value } = e.target;

    if (name === "groupName" || name === "description") {
      setForm((prev) => ({
        ...prev,
        [name]: toTitleCase(value),
      }));
    }
  };

  /* ================= SAVE ================= */
  const handleSubmit = async () => {
    if (!form.groupName.trim()) {
      alert("Group Name required");
      nameRef.current?.focus();
      return;
    }

    const payload = {
      ...form,
      createdBy: userId,
      modifiedBy: userId,
      entryDate: new Date().toISOString(),
      lastModifyDate: new Date().toISOString(),
    };

    try {
      await axiosMaster.post("/GroupMaster/Upsert", payload);
      onSuccess?.();
      onClose?.();
    } catch (err) {
      console.error("Save error:", err);
      alert("Failed to save group");
    }
  };

  /* ================= UI ================= */
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 border">
        <h2 className="text-lg font-bold mb-5">
          {form.id ? "Edit Group" : "Add Group"}
        </h2>

        <div className="space-y-4">
          {/* ✅ Group Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Group Name <span className="text-red-500">*</span>
            </label>
            <input
              ref={nameRef}
              type="text"
              name="groupName"
              value={form.groupName}
              onChange={handleChange}
              onBlur={handleBlurFormat}
              onFocus={() => handleAutoSelect(nameRef)}
              onKeyDown={(e) => handleKeyDown(e, descRef, null)}
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* ✅ Description */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Description
            </label>
            <textarea
              ref={descRef}
              name="description"
              value={form.description}
              onChange={handleChange}
              onBlur={handleBlurFormat}
              onFocus={() => handleAutoSelect(descRef)}
              onKeyDown={(e) => handleKeyDown(e, statusRef, nameRef)}
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
              rows={3}
            />
          </div>

          {/* ✅ Status */}
          <div className="flex items-center gap-2">
            <input
              ref={statusRef}
              type="checkbox"
              name="status"
              checked={form.status}
              onChange={handleChange}
              onKeyDown={(e) => handleKeyDown(e, saveRef, descRef)}
            />
            <label className="text-sm font-medium text-slate-700">
              Active
            </label>
          </div>

          {/* ✅ Buttons */}
          <div className="flex justify-end gap-2 pt-3">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-gray-500 text-white hover:bg-gray-600"
            >
              Cancel
            </button>

            <button
              ref={saveRef}
              onClick={handleSubmit}
              onKeyDown={(e) => handleKeyDown(e, null, statusRef)}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddEditGroupMaster;